/*
,--.    ,-----.  ,-----.  ,---.  ,--.    ,------.  ,-----.
|  |   '  .-.  ''  .--./ /  O  \ |  |    |  .-.  \ |  |) /_
|  |   |  | |  ||  |    |  .-.  ||  |    |  |  \  :|  .-.  \
|  '--.'  '-'  ''  '--'\|  | |  ||  '--. |  '--'  /|  '--' /
`-----' `-----'  `-----'`--' `--'`-----' `-------' `------'

LOCAL DB MAINTAIN ALL THE USER DATA THAT NOT RELATED TO THE UI.
LOCAL DB HAS TWO PARTS:

    - CACHE DB: ALL REAL TIME DATA FROM SERVER WILL BE CACHED HERE FOR FUTURE USE

    - OFFLINE DB: ALL USER CHANGES MADE IN OFFLINE MODE WILL BE STORED HERE AND RETRY
                    WHEN THE CONNECTION RESTORED
*/

/// ///////////////////////////////////////////////////////////////////////////////////////

/// ///////////////////////////////////////////////////////////////////////////////////////

/*
,--.  ,--.,------.,--.   ,------. ,------.,------.
|  '--'  ||  .---'|  |   |  .--. '|  .---'|  .--. '
|  .--.  ||  `--, |  |   |  '--' ||  `--, |  '--'.'
|  |  |  ||  `---.|  '--.|  | --' |  `---.|  |\  \
`--'  `--'`------'`-----'`--'     `------'`--' '--'
 ,---. ,--------.,------. ,--.,--.  ,--. ,----.
'   .-''--.  .--'|  .--. '|  ||  ,'.|  |'  .-./
`.  `-.   |  |   |  '--'.'|  ||  |' '  ||  | .---.
.-'    |  |  |   |  |\  \ |  ||  | `   |'  '--'  |
`-----'   `--'   `--' '--'`--'`--'  `--' `------'
*/

/// ///////////////////////////////////////////////////////////////////////////////////////
/*
 ,---.  ,------.,--------.,--. ,--.,------.
'   .-' |  .---''--.  .--'|  | |  ||  .--. '
`.  `-. |  `--,    |  |   |  | |  ||  '--' |
.-'    ||  `---.   |  |   '  '-'  '|  | --'
`-----' `------'   `--'    `-----' `--'
*/
/* eslint no-return-await: 0 */

import Dexie from 'dexie'
import fireService from './firebase'
import settings from './settings'

const CONSOLE_TAG_GREEN = 'color: white; background-color: #2ecc71; height: 16px; border-radius: 8px; display: inline-block; padding-left: 4px; padding-right: 4px;'
const CONSOLE_TAG_ORANGE = 'color: white; background-color: #f39c12; height: 16px; border-radius: 8px; display: inline-block; padding-left: 4px; padding-right: 4px;'
const CONSOLE_TAG_RED = 'color: white; background-color: #e74c3c; height: 16px; border-radius: 8px; display: inline-block; padding-left: 4px; padding-right: 4px;'
const CONSOLE_TAG_BLUE = 'color: white; background-color: #3498db; height: 16px; border-radius: 8px; display: inline-block; padding-left: 4px; padding-right: 4px;'
// const AUTHOR_ICON = 'display: block; height: 100px; width: 100px; background: url(https://i.postimg.cc/qtv4ypzj/photo-2019-04-02-15-25-43.jpg) no-repeat; font-size:150px;'

const localDB = new Dexie('localDB')
const localStore = window.localStorage

// local db runtime states
var localStates = {
  networkOnline: window.navigator.onLine,
  forceOfflineMode: JSON.parse(localStore.getItem('forceOfflineMode')) || settings.forceOfflineOnInit
}

if (localStates.forceOfflineMode) {
  console.log('%c Setting ', CONSOLE_TAG_ORANGE, 'Force offline mode on init has enabled, to disable, change in setting file.')
}

var storageEstimate = {
  total: null,
  used: null
}

// Display current storage estimate
if ('storage' in navigator && 'estimate' in navigator.storage) {
  navigator.storage.estimate().then(({ usage, quota }) => {
    updateStorageEstimate({
      total: quota,
      used: usage
    })
  })
}

// watch for the network status change
window.addEventListener('online', goOnline)
window.addEventListener('offline', goOffline)

// create local db
localDB.version(1).stores({
  formQuestions: 'id',
  formTemplates: 'templateId',
  items: 'id, projectId, name',
  folders: 'id, projectId, name',
  offlineFolders: 'id, projectId, name',
  offlineItems: 'id, projectId, name',
  currentUser: 'status',
  users: 'uid',
  projects: 'id'
})

// put blank user in the table if no table yet
localDB.currentUser.count((count) => {
  if (count === 0) {
    // console.log('INIT BLANK USER')
    localDB.currentUser.put({
      status: 'ACTIVE', // status is primary key, to ensure only one user can be stored locally
      uid: 'BLANK_USER'
    }).catch(err => {
      console.warn('Cannot init blank user =>', err)
    })
  }
})

// listen to event from firebase service
document.addEventListener('FIRE-SERVICE-EVENT-TO-LOCAL-DB', handleIncomingEvent)

/// ///////////////////////////////////////////////////////////////////////////////////////
/*
  ,---.  ,------. ,--.
 /  O  \ |  .--. '|  | ,---.
|  .-.  ||  '--' ||  |(  .-'
|  | |  ||  | --' |  |.-'  `)
`--' `--'`--'     `--'`----'
*/

export default {
  initApp: initApp,
  authSignIn: authSignIn,
  authSignOut: authSignOut,
  signUpUser: signUpUser,
  resetPassword: resetPassword,
  initData: initData,
  switchProject: switchProject,
  addProject: addProject,
  getRandomId: getRandomId,
  getOfflineFoldersOffline: getOfflineFoldersOffline,
  enableOfflineMode: enableOfflineMode,
  disableOfflineMode: disableOfflineMode,
  setFolder: setFolder,
  clearOfflineFolder: clearOfflineFolder,
  setItem: setItem,
  getOfflineItemsOffline: getOfflineItemsOffline,
  getItemData: getItemData,
  uploadImage: uploadImage,
  sendInvitationEmail: sendInvitationEmail,
  changeUserStatus: changeUserStatus,
  changeUserScope: changeUserScope,
  changeProjectSetting: changeProjectSetting,
  getStorageEstimate: getStorageEstimate
}

function enableOfflineMode () {
  localStates.forceOfflineMode = true
  localStore.setItem('forceOfflineMode', 'true')
  console.log('%c Network : Offline ', CONSOLE_TAG_ORANGE, 'Force offline mode enabled!')
  sendEvent('VUEX', 'forceOfflineModeChanged', true)
}

function disableOfflineMode () {
  localStates.forceOfflineMode = false
  localStore.setItem('forceOfflineMode', 'false')
  // console.log('ONLINE MODE => ', onlineMode())
  if (onlineMode()) {
    console.log('%c Network : Online ', CONSOLE_TAG_GREEN, 'Now running under online mode!')
    onlineInit()
    uploadOfflineData()
  }
  sendEvent('VUEX', 'forceOfflineModeChanged', false)
}

async function initApp () {
  // start local init
  console.group('%c App : Initialization ', CONSOLE_TAG_BLUE, 'App initialization start')
  let localInitResult = await localInit()
  if (!localInitResult.success) {
    console.log('%c App : Local Initialization ', CONSOLE_TAG_RED, localInitResult.error)
    console.log('%c App : Initialization ', CONSOLE_TAG_RED, 'App initialization fail')
    console.groupEnd()

    return {
      success: false,
      error: 'Local initialization fail'
    }
  }
  if (onlineMode()) {
    let onlineInitResult = await onlineInit()
    if (!onlineInitResult.success) {
      console.log('%c App : Online Initialization', CONSOLE_TAG_RED, 'Online Initialization failed.')
      console.log('%c App : Initialization', CONSOLE_TAG_RED, 'App initialization fail')
      console.groupEnd()

      return {
        success: false,
        error: 'Online initialization failed'
      }
    }
  }
  console.log('%c App : Initialization ', CONSOLE_TAG_GREEN, 'App initialization success')
  console.groupEnd()
  return {
    success: true,
    error: ''
  }
}

async function authSignIn (signInRequest) {
  if (!onlineMode()) {
    return {
      success: false,
      error: 'Unable to sign in from offline'
    }
  }
  return await fireService.authSignIn(signInRequest)
}

async function authSignOut () {
  fireService.authSignOut()
}

async function signUpUser (signUpRequest) {
  if (!onlineMode()) {
    return {
      success: false,
      error: 'Your device is current offline'
    }
  }
  // human challenge
  if (!await fireService.verifyReCaptcha(signUpRequest.token)) {
    return {
      success: false,
      error: 'Unable to pass human test.'
    }
  }
  // create temp user
  let createTempUserResult = await fireService.createTempUser(signUpRequest)
  if (!createTempUserResult.success) {
    return {
      success: false,
      error: createTempUserResult.error
    }
  }
  // create auth user
  let createAuthResult = await fireService.createNewAuthUser(signUpRequest)
  if (!createAuthResult.success) {
    return {
      success: false,
      error: createAuthResult.error
    }
  }
  // user sign up success
  return {
    success: true,
    error: ''
  }
}

async function resetPassword (email) {
  if (!onlineMode()) {
    return {
      success: false,
      error: 'Unable to send password reset email from offline'
    }
  }
  return await fireService.resetPassword(email)
}

async function initData () {
  // load data from local db (this will be update immediately after the connection of firestore)

  // loadLocalData()

  // connect all firestore listener
  fireService.connect()
}

async function switchProject (projectId) {
  if (onlineMode()) {
    // if online, load directly from server
    let switchOnlineResult = await fireService.switchProjectOnline(projectId)
    if (switchOnlineResult.success) {
      return {
        success: true,
        error: ''
      }
    }
  }
  // if offline mode, listener still will be linked in offline link function, but the local data will be set first
  return switchProjectOffline(projectId)
}

async function addProject (projectInfo) {
  if (!onlineMode()) {
    return {
      success: false,
      error: 'Unable to create new project from offline'
    }
  }
  if (typeof projectInfo.id !== 'string') {
    return {
      success: false,
      error: 'Invalid project ID'
    }
  }
  return await fireService.addProject(projectInfo)
}

function getRandomId (collection) {
  return fireService.getRandomId(collection)
}

async function getOfflineFoldersOffline (currentProject) {
  let folders = []
  if (currentProject === null) {
    return []
  }
  let projectId = currentProject.id
  // console.log('project id >', projectId)
  if (typeof projectId !== 'string') {
    // console.warn('Invalid projectId', projectId)
    return folders
  }
  try {
    folders = await localDB.offlineFolders.where('projectId').equals(projectId).sortBy('name')
    console.groupCollapsed('%c Data : Offline Folders ', CONSOLE_TAG_BLUE, 'Fetch offline folders')
    console.log('folders =>', folders)
    console.groupEnd()
    return folders.filter(folder => folder.open)
  } catch (err) {
    console.warn('Cannot get offline folders')
    return []
  }
}

async function setFolder (folder) {
  if (typeof folder !== 'object') {
    return {
      success: false,
      error: 'Missing folder information'
    }
  }
  if (onlineMode()) {
    return await fireService.setFolderOnline(folder)
  } else {
    return await setFolderOffline(folder)
  }
}

async function clearOfflineFolder (folderId) {
  if (typeof folderId !== 'string') {
    console.warn('Folder id must be a string')
    return {
      success: false,
      error: 'Folder id must be a string'
    }
  }
  await localDB.offlineFolders.delete(folderId)
  sendEvent('VUEX', 'refreshCurrentOfflineFolders', null)
  return {
    success: true,
    error: ''
  }
}

async function setItem (itemData) {
  if (onlineMode()) {
    return await fireService.setItemOnline(itemData)
  } else {
    return await setItemOffline(itemData)
  }
}

async function getOfflineItemsOffline (currentProject) {
  let items = []
  if (currentProject === null) {
    return []
  }
  let projectId = currentProject.id
  // console.log('project id >', projectId)
  if (typeof projectId !== 'string') {
    console.warn('Invalid projectId', projectId)
    return items
  }
  try {
    items = await localDB.offlineItems.where('projectId').equals(projectId).sortBy('name')
    // console.log(items)
    return items.filter(item => item.open)
  } catch (err) {
    console.warn('Cannot get offline items')
    return []
  }
}

async function getItemData (itemId) {
  // INVOKE THE ITEM EDITOR AND EDIT THE ITEM
  if (itemId === '' || (typeof itemId) !== 'string') {
    console.warn('Invalid itemId =>', itemId)
    return undefined
  }
  try {
    return await localDB.items.get(itemId) || await localDB.offlineItems.get(itemId)
  } catch (err) {
    console.warn(err)
    return undefined
  }
}

async function uploadImage (file) {
  if (onlineMode()) {
    return await fireService.uploadImageOnline(file)
  } else {
    return uploadImageOffline(file)
  }
}

async function sendInvitationEmail (email) {
  if (!onlineMode()) {
    return {
      success: false,
      error: 'Cannot send invitation while offline'
    }
  }
  return await fireService.sendInvitationEmail(email)
}

async function changeUserStatus (userStatusChangeRequest) {
  if (!onlineMode()) {
    return {
      success: false,
      error: 'Cannot change user status while offline'
    }
  }
  return await fireService.changeUserStatus(userStatusChangeRequest)
}

async function changeUserScope (userScopeChangeRequest) {
  if (!onlineMode()) {
    return {
      success: false,
      error: 'Cannot change user role while offline'
    }
  }
  return await fireService.changeUserScope(userScopeChangeRequest)
}

async function changeProjectSetting (project) {
  if (!onlineMode()) {
    return {
      success: false,
      error: 'Cannot save changes while offline'
    }
  }
  return await fireService.changeProjectSetting(project)
}

/// ///////////////////////////////////////////////////////////////////////////////////////
/*
,--.  ,--.,------.,--.   ,------. ,------.,------.
|  '--'  ||  .---'|  |   |  .--. '|  .---'|  .--. '
|  .--.  ||  `--, |  |   |  '--' ||  `--, |  '--'.'
|  |  |  ||  `---.|  '--.|  | --' |  `---.|  |\  \
`--'  `--'`------'`-----'`--'     `------'`--' '--'

,------.,--. ,--.,--.  ,--. ,-----.,--------.,--. ,-----. ,--.  ,--.
|  .---'|  | |  ||  ,'.|  |'  .--./'--.  .--'|  |'  .-.  '|  ,'.|  |
|  `--, |  | |  ||  |' '  ||  |       |  |   |  ||  | |  ||  |' '  |
|  |`   '  '-'  '|  | `   |'  '--'\   |  |   |  |'  '-'  '|  | `   |
`--'     `-----' `--'  `--' `-----'   `--'   `--' `-----' `--'  `--'

*/
function handleIncomingEvent (event) {
  // console.log('receive incoming event')
  let payload = event.detail
  switch (payload.event) {
    case 'hello':
      // console.log(payload.data)
      break
    case 'currentUser':
      updateLocalUser(payload.data)
      break
    case 'authStateChange':
      handleAuthStateChange(payload.data)
      break
    case 'updateProjects':
      updateProjects(payload.data)
      break
    case 'updateFormTemplates':
      updateFormTemplates(payload.data)
      break
    case 'updateFormQuestions':
      updateFormQuestions(payload.data)
      break
    case 'updateUsers':
      updateUsers(payload.data)
      break
    case 'updateCurrentProject':
      updateCurrentProject(payload.data)
      break
    case 'updateCurrentFolders':
      updateCurrentFolders(payload.data)
      break
    case 'updateCurrentItems':
      updateCurrentItems(payload.data)
      break
    case 'clearOfflineFolder':
      clearOfflineFolder(payload.data)
      break
  }
}

function onlineMode () {
  // determine which data proto will be used
  return localStates.networkOnline && !localStates.forceOfflineMode
}

function goOnline () {
  localStates.networkOnline = true
  if (onlineMode()) {
    console.log('% Network : Online ', CONSOLE_TAG_GREEN, 'Now running under online mode!')
    onlineInit()
  }
  sendEvent('VUEX', 'deviceNetworkChange', true)
}
function goOffline () {
  localStates.networkOnline = false
  if (!onlineMode()) {
    console.log('%c Network : Offline ', CONSOLE_TAG_ORANGE, 'Now running under offline mode!')
  }
  sendEvent('VUEX', 'deviceNetworkChange', false)
}

async function localInit () {
  console.log('%c App : Local Initialization ', CONSOLE_TAG_BLUE, 'Local Initialization start')
  try {
    let user = await getUserOffline()
    if (!user) {
      console.log('%c User ', CONSOLE_TAG_BLUE, 'No local user')
      return {
        success: true,
        error: 'No local user'
      }
    }
    console.log('%c User ', CONSOLE_TAG_GREEN, 'Sign in by local user record')
    sendEvent('VUEX', 'currentUser', user)
    let projects = await loadProjectsOffline()
    sendEvent('VUEX', 'updateProjects', projects)
    let formTemplates = await loadFormTemplatesOffline()
    sendEvent('VUEX', 'updateFormTemplates', formTemplates)
    let formQuestions = await loadFormQuestionsOffline()
    sendEvent('VUEX', 'updateFormQuestions', formQuestions)

    return {
      success: true,
      error: ''
    }
  } catch (err) {
    return {
      success: false,
      error: err
    }
  }
}

async function onlineInit () {
  let onlineInitResult = await fireService.onlineInit()

  uploadOfflineData()

  if (!onlineInitResult.success) {
    console.log('%c App : Online Initialization ', CONSOLE_TAG_RED, onlineInitResult.error)
    return {
      success: false,
      error: 'Online initialization fail'
    }
  } else {
    return {
      success: true,
      error: ''
    }
  }
}

async function loadProjectsOffline () {
  let projects = []
  try {
    let projectsAmount = await localDB.projects.count()
    if (projectsAmount !== 0) {
      projects = await localDB.projects.toArray()
      projects.sort((projA, projB) => {
        return projB.createOn - projA.createOn
      })
    }
  } catch (error) {
    console.warn(error)
  }
  console.groupCollapsed('%c Data : Projects', CONSOLE_TAG_BLUE, 'Load offline projects')
  console.log('Local projects =>', projects)
  console.groupEnd()
  return projects
}

async function loadFormTemplatesOffline () {
  let templates = []
  try {
    let templatesAmount = await localDB.formTemplates.count()
    if (templatesAmount !== 0) {
      templates = await localDB.formTemplates.toArray()
      templates.sort((tempA, tempB) => {
        return tempA.templateId.localeCompare(tempB.templateId)
      })
    }
  } catch (error) {
    console.warn(error)
  }
  console.groupCollapsed('%c Data : Templates', CONSOLE_TAG_BLUE, 'Load offline templates')
  console.log('Local templates =>', templates)
  console.groupEnd()
  return templates
}

async function loadFormQuestionsOffline () {
  let questions = []
  try {
    let questionsAmount = await localDB.formQuestions.count()
    if (questionsAmount !== 0) {
      questions = await localDB.formQuestions.toArray()
      questions.sort((qA, qB) => {
        return qA.id.localeCompare(qB.id)
      })
    }
  } catch (error) {
    console.warn(error)
  }
  console.groupCollapsed('%c Data : Questions', CONSOLE_TAG_BLUE, 'Load offline questions')
  console.log('Local questions =>', questions)
  console.groupEnd()
  return questions
}

async function handleAuthStateChange (user) {
  if (user === null) {
    console.groupCollapsed('%c User ', CONSOLE_TAG_BLUE, 'User auth state change detected')
    console.log('User token cleared (May caused by sign out request from user)')
    console.groupEnd()
    userSignedOut()
  } else {
    // test if local user is okay to use (if local user match, uid, user will be login)
    try {
      await userSignedIn(user.uid, user.email)
      console.groupCollapsed('%c User ', CONSOLE_TAG_GREEN, 'User Signed In Success')
      console.log(user.email)
      console.groupEnd()

      initData()
    } catch (error) {
      console.groupCollapsed('%c User ', CONSOLE_TAG_RED, 'User Signed In Failed')
      console.log(error)
      console.groupEnd()
    }
  }
}

async function offlineSignProcess (uid) {
  console.groupCollapsed('%c User ', CONSOLE_TAG_BLUE, 'Start Offline Sign Process')
  console.log(`uid => *****************${uid.slice(-4)}`)
  console.groupEnd()
  if (!uid) {
    console.groupCollapsed('%c User ', CONSOLE_TAG_RED, 'Offline Sign Process Failed')
    console.log('No UID for offline sign in process')
    console.groupEnd()

    return {
      success: false,
      error: 'No UID for offline sign in process',
      user: null
    }
  }
  let currentUser = await getCurrentUser(uid)
  if (currentUser !== null) {
    console.groupCollapsed('%c User ', CONSOLE_TAG_GREEN, 'Offline Sign Process Success')
    console.log(currentUser)
    console.groupEnd()
    return {
      success: true,
      error: '',
      user: currentUser
    }
  }
}

async function getCurrentUser (uid) {
  // Send the current user data if uid match the current user
  try {
    // console.log('get offline user=>', uid)
    let userData = await localDB.currentUser.get('ACTIVE')
    if (userData === undefined) {
      // local user table error, rebuild the table
      localDB.currentUser.put({
        status: 'ACTIVE', // status is primary key, to ensure only one user can be stored locally
        uid: 'BLANK_USER'
      }).catch(err => {
        throw err
      })
      throw new Error('No local sign in status found')
    } else if (userData.uid !== uid) {
      // console.log('local user uid =>', userData.uid)
      // console.log('request user uid =>', uid)
      // auth status changed, clear current user
      localDB.currentUser.put({
        status: 'ACTIVE', // status is primary key, to ensure only on user can be stored locally
        uid: 'BLANK_USER'
      })
      throw new Error('No local sign in status found, uid not match')
    } else {
      return userData
    }
  } catch (error) {
    console.warn('getCurrentUser()', error)
    return null
  }
}

async function onlineSignInProcess (uid, email) {
  console.groupCollapsed('%c User ', CONSOLE_TAG_BLUE, 'Start Online Sign Process')
  console.log(`uid => *****************${uid.slice(-4)}`)
  console.groupEnd()
  if (!uid) {
    console.groupCollapsed('%c User ', CONSOLE_TAG_RED, 'Online Sign Process Failed')
    console.log('No UID for offline sign in process')
    console.groupEnd()
    return {
      success: false,
      error: 'No UID for online sign in Process'
    }
  }
  let recordSignInResult = await fireService.recordSignIn(uid, email)
  if (!recordSignInResult.success) {
    console.groupCollapsed('%c User ', CONSOLE_TAG_RED, 'Online Sign Process Failed')
    console.log(recordSignInResult.error)
    console.log('Auth sign out...')
    fireService.authSignOut()
    console.groupEnd()
    return {
      success: false,
      error: 'Cannot record sign in',
      errorDetail: recordSignInResult.error
    }
  }
  // listen to user status change
  console.groupCollapsed('%c User ', CONSOLE_TAG_BLUE, 'Start listen to user data')
  console.log(`uid => *****************${uid.slice(-4)}`)
  console.groupEnd()
  fireService.listenToUser(uid)
}

async function updateLocalUser (userData) {
  if (userData === null) {
    try {
      await localDB.currentUser.put({
        status: 'ACTIVE',
        uid: 'BLANK_USER'
      })
      console.log('%c User ', CONSOLE_TAG_BLUE, 'Local user cleared.')
      sendEvent('VUEX', 'currentUser', null)
    } catch (err) {
      console.log('%c User', CONSOLE_TAG_RED, 'Update local user failed =>', err)
    }
  } else {
    try {
      await localDB.currentUser.put(userData)
      sendEvent('VUEX', 'currentUser', userData)
    } catch (err) {
      console.warn('Update local user failed =>', err)
    }
  }
}

function sendEvent (target, eventName, data) {
  if (typeof eventName !== 'string') {
    console.warn('event name must be a string, is =>', eventName)
    return
  }
  let payload = {
    event: eventName,
    data: data
  }
  if (target === 'VUEX') {
    // console.log('Send event to VUEX')
    var event = new CustomEvent('LOCAL-DB-EVENT-TO-VUEX', { detail: payload })
  } else {
    console.warn('Unknown event target =>', target)
  }
  document.dispatchEvent(event)
}

async function userSignedIn (uid, email) {
  if (onlineMode()) {
    // console.log('sign in user in online mode')
    return await onlineSignInProcess(uid, email)
  } else {
    // console.log('sign in user in offline mode')
    let offlineSignInResult = await offlineSignProcess(uid)
    if (offlineSignInResult.success) {
      sendEvent('VUEX', 'currentUser', offlineSignInResult.user)
      return offlineSignInResult
    }
  }
}

async function userSignedOut () {
  updateLocalUser(null)
}

async function updateProjects (projectList) {
  try {
    await localDB.projects.bulkPut(projectList)
    console.log('%c Data : Projects', CONSOLE_TAG_BLUE, `Updated ${projectList.length} projects in local database.`)
    sendEvent('VUEX', 'updateProjects', projectList)
  } catch (error) {
    console.warn(error)
  }
}

async function updateFormTemplates (templateList) {
  try {
    await localDB.formTemplates.bulkPut(templateList)
    console.log('%c Data : Templates', CONSOLE_TAG_BLUE, `Updated ${templateList.length} templates in local database.`)
    sendEvent('VUEX', 'updateFormTemplates', templateList)
  } catch (error) {
    console.warn(error)
  }
}

async function updateFormQuestions (questionList) {
  try {
    await localDB.formQuestions.bulkPut(questionList)
    console.log('%c Data : Questions', CONSOLE_TAG_BLUE, `Updated ${questionList.length} questions in local database.`)
    sendEvent('VUEX', 'updateFormQuestions', questionList)
  } catch (error) {
    console.warn(error)
  }
}

async function updateUsers (userList) {
  try {
    await localDB.users.bulkPut(userList)
    console.log('%c Data : Users', CONSOLE_TAG_BLUE, `Updated ${userList.length} users in local database.`)
    sendEvent('VUEX', 'updateUsers', userList)
  } catch (error) {
    console.warn(error)
  }
}

async function updateCurrentProject (data) {
  localDB.projects.put(data)
  sendEvent('VUEX', 'updateCurrentProject', data)
}

async function updateCurrentFolders (data) {
  localDB.folders.bulkPut(data)
  sendEvent('VUEX', 'updateCurrentFolders', data)
}

async function updateCurrentItems (data) {
  localDB.items.bulkPut(data)
  sendEvent('VUEX', 'updateCurrentItems', data)
}

async function switchProjectOffline (projectId) {
  try {
    let projectData = await localDB.projects.get(projectId)
    sendEvent('VUEX', 'updateCurrentProject', projectData)
    let items = await getItemsOffline(projectId)
    sendEvent('VUEX', 'updateCurrentItems', items)
    let folders = await getFoldersOffline(projectId)
    sendEvent('VUEX', 'updateCurrentFolders', folders)
    sendEvent('VUEX', 'refreshCurrentOfflineFolders')
    sendEvent('VUEX', 'refreshCurrentOfflineItems')

    fireService.switchProjectOnline(projectId)
    return {
      success: true,
      error: ''
    }
  } catch (err) {
    console.warn(err)
    return {
      success: false,
      error: ''
    }
  }
}

async function getUserOffline () {
  try {
    let localUser = await localDB.currentUser.get('ACTIVE')
    if (!localUser || localUser.uid === 'BLANK_USER') {
      return null
    } else {
      return localUser
    }
  } catch (error) {
    console.log('%c User ', CONSOLE_TAG_RED, error)
    return null
  }
}

async function getItemsOffline (projectId) {
  let items = []
  // console.log('project id >', projectId)

  if (typeof projectId !== 'string') {
    console.warn('Invalid projectId', projectId)
    return items
  }
  try {
    items = await localDB.items.where('projectId').equals(projectId).sortBy('name')
    return items.filter(item => item.open)
  } catch (err) {
    console.warn('Cannot get offline items')
    return []
  }
}

async function getFoldersOffline (projectId) {
  let folders = []
  // console.log('project id >', projectId)
  if (typeof projectId !== 'string') {
    console.warn('Invalid projectId', projectId)
    return folders
  }
  try {
    folders = await localDB.folders.where('projectId').equals(projectId).sortBy('name')
    return folders.filter(folder => folder.open)
  } catch (err) {
    console.warn('Cannot get offline folders')
    return []
  }
}

async function setFolderOffline (folder) {
  if (typeof folder !== 'object') {
    return {
      success: false,
      error: 'Missing folder info'
    }
  }
  try {
    await localDB.offlineFolders.put(folder)
    sendEvent('VUEX', 'refreshCurrentOfflineFolders')
    // delete the instance in the online cache db
    await localDB.folders.delete(folder.id)
    sendEvent('VUEX', 'updateCurrentFolders', await getFoldersOffline(folder.projectId))
    return {
      success: true,
      error: ''
    }
  } catch (error) {
    console.warn(error)
    return {
      success: false,
      error: 'Unable to save the change, please try again later'
    }
  }
}

async function uploadOfflineData () {
  // console.log('Start auto uploading')
  if (!onlineMode()) {
    return
  }
  uploadOfflineFolders()
  uploadOfflineItems()
}

async function uploadOfflineFolders () {
  try {
    let offlineFoldersCounts = await localDB.offlineFolders.count()
    if (offlineFoldersCounts === 0) {
      console.log('%c Data : Offline Folders ', CONSOLE_TAG_BLUE, 'No offline folders to upload')
      return
    }
    let offlineFolders = await localDB.offlineFolders.toArray()
    let uploadResult = await Promise.all(offlineFolders.map(
      async (folder) => {
        return await fireService.setFolderOnline(folder)
      }
    ))
    console.log(uploadResult)
    let deleteList = uploadResult.filter(result => result.success).map(result => { return result.folder.id })
    await localDB.offlineFolders.bulkDelete(deleteList)
    sendEvent('VUEX', 'refreshCurrentOfflineFolders')
  } catch (error) {
    console.groupCollapsed('%c Data : Offline Folders ', CONSOLE_TAG_ORANGE, 'Unable to upload offline Folders')
    console.log(error)
    console.groupEnd()
  }
}

async function uploadOfflineItems () {
  try {
    let offlineItemsCounts = await localDB.offlineItems.count()
    if (offlineItemsCounts === 0) {
      console.log('%c Data : Offline Items ', CONSOLE_TAG_BLUE, 'No offline items to upload')
      return
    }
    let offlineItems = await localDB.offlineItems.toArray()
    let uploadResult = await Promise.all(offlineItems.map(
      async (item) => {
        return await fireService.setItemOnline(item)
      }
    ))
    // console.log('upload items',uploadResult)
    let deleteList = uploadResult.filter(result => result.success).map(result => { return result.item.id })
    await localDB.offlineItems.bulkDelete(deleteList)
    // console.log(`${deleteList.length} / ${offlineItems.length} offline items has been uploaded`)
    sendEvent('VUEX', 'refreshCurrentOfflineItems')
  } catch (error) {
    console.groupCollapsed('%c Data : Offline Items ', CONSOLE_TAG_ORANGE, 'Unable to upload offline Items')
    console.log(error)
    console.groupEnd()
  }
}

async function setItemOffline (itemData) {
  if (typeof itemData !== 'object') {
    return {
      success: false,
      error: 'Missing folder info'
    }
  }
  try {
    await localDB.offlineItems.put(itemData)
    sendEvent('VUEX', 'refreshCurrentOfflineItems')
    // delete the instance in the online cache db
    await localDB.items.delete(itemData.id)
    sendEvent('VUEX', 'updateCurrentItems', await getItemsOffline(itemData.projectId))
    return {
      success: true,
      error: '',
      item: itemData
    }
  } catch (error) {
    console.warn(error)
    return {
      success: false,
      error: 'Unable to save the change, please try again later'
    }
  }
}

function uploadImageOffline (file) {
  if (typeof file !== 'string') {
    return {
      success: false,
      error: 'Invalid data format(must be a base64 string)'
    }
  }
  let contentType = (file.split(';')[0]).split(':')[1]
  if (!['image/jpeg', 'image/png'].includes(contentType)) {
    return {
      success: false,
      error: 'Only JPEG and PNG are support.'
    }
  }
  return {
    success: true,
    url: file
  }
}

function updateStorageEstimate (usage) {
  storageEstimate = usage
  console.groupCollapsed('%c App : Available Storage ', CONSOLE_TAG_BLUE, `${(usage.total / 1048576).toFixed(1)} MB [${(100 - 100 * usage.used / usage.total).toFixed(3)} %]`)
  console.log(`Using ${(usage.used / 1048576).toFixed(1)} MB out of ${(usage.total / 1048576).toFixed(1)} MB. storage [${(100 - 100 * usage.used / usage.total).toFixed(3)} %].`)
  console.groupEnd()
}

function getStorageEstimate () {
  return storageEstimate
}
